import get from "lodash/get"
import * as Sentry from "@sentry/browser"

import externalKeys from "constants/externalKeys"
import exceptions from "constants/exceptions"

import { getMap } from "redux/mappers"

import { environmentTypes } from "constants/environment"

import {
    selectAuthToken,
    selectIsRefreshingAuthToken,
    selectRefreshToken,
} from "redux/reducers/core/authentication"
import {
    tokenRefresh,
    logout,
} from "redux/reducers/core/authentication/actions"
import { LOGOUT } from "redux/reducers/core/authentication/actionNames"
import { createAddRequestAction } from "redux/reducers/core/requestQueue/actions"
import { selectHasLoggedInUserAgreedToAllPolicies } from "redux/reducers/entities/users"
import { createLoadLoggedInUserAction } from "redux/reducers/entities/users/actions"

import { ERROR, LOADED, LOADING } from "./actionNames"

export const addTokenToRequest = (state, request) => {
    const authToken = selectAuthToken(state)
    const preppedRequest = { ...request }

    if (authToken) {
        preppedRequest.headers = {
            ...preppedRequest.headers,
            Authorization: `Bearer ${authToken}`,
        }
    }

    if (process.env.REACT_APP_DIAGNOSTICS === "TRUE") {
        preppedRequest.headers = {
            ...preppedRequest.headers,
            "x-diagnostics": externalKeys.diagnosticsAPIKey,
        }
    }

    if (!request.external) {
        preppedRequest.headers = {
            ...preppedRequest.headers,
            "X-Client-Type": "web",
            "X-Client-Version": process.env.REACT_APP_VERSION,
        }
    }

    return preppedRequest
}

export const createApiClient = client => store => next => action => {
    function makeAction(status, data) {
        const newAction = { ...action, type: action.type + status, ...data }
        delete newAction.request
        return newAction
    }

    const { callback } = action

    if (!action || !action.request) {
        const actionResult = next(action)

        if (callback) {
            const { dispatch, getState } = store

            callback(dispatch, getState, action.payload)
        }

        return actionResult
    }

    next(makeAction(LOADING, false))
    if (selectIsRefreshingAuthToken(store.getState())) {
        return store.dispatch(createAddRequestAction(action))
    }

    let { request } = action

    if (!request.url.startsWith("http")) {
        request = addTokenToRequest(store.getState(), action.request)
    }

    return client.request(request).then(
        result => {
            const mapper = getMap(action.type)
            // Added to maintain a pattern in the result that is given to our reducer
            const finalRes = get(result, "data.data") || get(result, "data")
            const payload = mapper(store.getState, finalRes, action.payload)

            next(makeAction(LOADED, { payload }))

            if (callback) {
                const { dispatch, getState } = store

                callback(dispatch, getState, payload)
            }
        },
        error => {
            if (action.type === LOGOUT) {
                next(
                    makeAction(LOADED, {
                        payload: {
                            result: null,
                            originalPayload: action.payload,
                        },
                    }),
                )
            }

            const state = store.getState()
            const isRefreshing = selectIsRefreshingAuthToken(state)
            const exceptionType = get(error, "response.data.Type")
            const currentToken = selectAuthToken(state)

            switch (exceptionType) {
                // Check if request failed due to server requested logout
                case exceptions.sessionNotFound:
                case exceptions.sessionSignOut: {
                    if (isRefreshing) {
                        // store current request in the queue so we can
                        // restart when we have a new token.
                        store.dispatch(createAddRequestAction(action))
                    } else if (
                        `Bearer ${currentToken}` !==
                        get(request, "headers.Authorization")
                    ) {
                        // It is possible the auth token was updated while this request happened.
                        store.dispatch(action)
                    } else {
                        store.dispatch(logout())
                    }

                    break
                }

                // if the request failed because of a login issue
                // (exception type: SessionExpiredException)
                case exceptions.sessionExipired: {
                    // It is possible the auth token was updated while this request happened.
                    if (
                        `Bearer ${currentToken}` !==
                        get(request, "headers.Authorization")
                    ) {
                        store.dispatch(action)
                    } else {
                        const refreshToken = selectRefreshToken(state)

                        // store current request in the queue so we can restart when we have a new token
                        store.dispatch(createAddRequestAction(action))

                        if (!isRefreshing) {
                            // Only request a new token if we're not currently refreshing.
                            store.dispatch(tokenRefresh(refreshToken))
                        }
                    }

                    break
                }
                case exceptions.agreeToPolicies: {
                    const isUserAgreedToAllPolicies =
                        selectHasLoggedInUserAgreedToAllPolicies(state)

                    if (isUserAgreedToAllPolicies) {
                        store.dispatch(createLoadLoggedInUserAction())
                    }

                    break
                }
                default: {
                    if (process.env.REACT_APP_ENV === environmentTypes.prod) {
                        Sentry.withScope(() => {
                            Sentry.captureException(error)
                        })
                    }

                    next(
                        makeAction(ERROR, {
                            payload: {
                                result: get(error, "response.data"),
                                originalPayload: action.payload,
                            },
                        }),
                    )
                    break
                }
            }
        },
    )
}

export default createApiClient
